import axios from 'axios';
import { TEST_API_BASE_URL, PRE_API_BASE_URL, BASE_URL } from '@/utils/config';
import { datafluxRum } from '@cloudcare/browser-rum';

const TOKEN_CACHE_FALLBACK_TTL_MS = 30 * 1000;
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
type TokenCacheEntry = {
  token: string;
  expireAt: number;
};
const tokenCacheByKey = new Map<string, TokenCacheEntry>();
const tokenInFlightByKey = new Map<string, Promise<string | null>>();

const decodeJwtPayload = (token: string): Record<string, any> | null => {
  try {
    const [, payloadPart] = token.split(".");
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (_) {
    return null;
  }
};

const getTokenExpireAt = (token: string) => {
  const payload = decodeJwtPayload(token);
  const expSeconds = payload?.exp;
  if (typeof expSeconds !== "number" || Number.isNaN(expSeconds)) {
    return Date.now() + TOKEN_CACHE_FALLBACK_TTL_MS;
  }
  return expSeconds * 1000 - TOKEN_REFRESH_BUFFER_MS;
};

const getTokenTemplateKey = () => {
  return process.env.REACT_APP_ENV === "production" ? "production" : "testing-template";
};

const getTokenTemplateArg = (templateKey: string) => {
  return templateKey === "production" ? undefined : { template: "testing-template" };
};

const getAuthTokenSingleFlight = async (): Promise<string | null> => {
  // @ts-ignore
  const session: any = window?.Clerk?.session;
  if (!session) return null;
  const templateKey = getTokenTemplateKey();
  // session/org/template 变化时使用不同缓存，避免拿到旧组织 token
  // @ts-ignore
  const activeOrgId = window?.Clerk?.organization?.id || session?.lastActiveOrganizationId || "no-org";
  const cacheKey = `${templateKey}::${session?.id || "unknown-session"}::${activeOrgId}`;
  const now = Date.now();
  const cached = tokenCacheByKey.get(cacheKey);
  if (cached && cached.expireAt > now) {
    return cached.token;
  }
  const existingInFlight = tokenInFlightByKey.get(cacheKey);
  if (existingInFlight) {
    return existingInFlight;
  }
  const tokenPromise = (async () => {
    const token = await session.getToken(getTokenTemplateArg(templateKey));
    if (token) {
      tokenCacheByKey.set(cacheKey, {
        token,
        expireAt: getTokenExpireAt(token),
      });
      return token;
    }
    tokenCacheByKey.delete(cacheKey);
    return null;
  })().finally(() => {
    tokenInFlightByKey.delete(cacheKey);
  });
  tokenInFlightByKey.set(cacheKey, tokenPromise);
  return tokenPromise;
};

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return TEST_API_BASE_URL;
  }
  if (process.env.REACT_APP_ENV === 'pre') {
    return PRE_API_BASE_URL;
  }
  if (process.env.REACT_APP_ENV === 'production') {
    return BASE_URL;
  }

  if (process.env.REACT_APP_ENV === 'development') {
    if (process.env.REACT_APP_API_BASE_URL) {
      // 通过 copy .env.example .env 文件配置
      return process.env.REACT_APP_API_BASE_URL;
    }
    return TEST_API_BASE_URL;
  }

  // Default to production API URL or add other environments as needed
  return BASE_URL; // replace with actual production API URL
};

export const API_BASE_URL = getApiBaseUrl();

console.log('API_BASE_URL', API_BASE_URL);

axios.defaults.withCredentials = true;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getAuthTokenSingleFlight();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // 请求错误处理
    return Promise.reject(error);
  }
);

// 设置拦截器处理响应
axiosInstance.interceptors.response.use(
  (response) => {
    
    // this should not happen but let's leave it as is for now
    if (response.data && response.data.code !== 0 && response.config.responseType !== 'blob') {
      // message.error(`non-zero non-error response: ${response.data.message}`);
      return Promise.reject({ 
        code: response.data.code,
        error: response.data.message 
      });
    }
    return response; // 如果没有错误，正常返回响应
  },
  (error) => {
    
    // 上报接口 HTTP 错误到观测云（不含 Network Error）
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const url = error.config?.url || error.config?.baseURL || 'unknown';
      const method = error.config?.method?.toUpperCase() || 'unknown';

      if (status !== 401 && typeof datafluxRum?.addError === 'function') {
        try {
          datafluxRum.addError(
            new Error(`API Error: ${method} ${url} - ${status}`),
            {
              source: 'axios',
              type: 'http_error',
              status,
              url,
              method,
              message: error.response?.data?.message || error.message,
              code: error.response?.data?.code,
            }
          );
        } catch (e) {
          // 静默失败，不影响主流程
        }
      }
    }
    // 网络错误（error.request && !error.response）不再上报

    if (error.response?.status === 401) {
      // 会话过期，跳转到登录页面
      // redirectToAuthPage();
      return Promise.reject({ message: error.response?.data?.message }); // 返回拒绝承诺阻止进一步处理
    }
    // 处理 HTTP 请求错误情况
    if (axios.isAxiosError(error) && error.response) {
      //TODO: remove global message error in the future to prevent duplication
      // message.error(error.response.data?.message);
      return Promise.reject({ message: error.response?.data?.message, payload: error.response?.data?.payload, code: error.response?.data?.code });
    }
    // 后续提示内容可标准化
    // message.error('Unknown Error');
    return Promise.reject(error?.response?.data); // Internal Server Error
  }
);

export default axiosInstance;
